import type { ButtonVariant } from "@replo/design-system/components/button/button-shared";
import type {
  BillingInterval,
  V1BillingTier,
  V2BillingTier,
} from "schemas/generated/billing";
import type { Workspace } from "schemas/generated/workspace";

import React from "react";

import { useIsWorkspaceOwner } from "@editor/hooks/useIsWorkspaceOwner";

import Button from "@replo/design-system/components/button/Button";
import { ArrowUpRight } from "lucide-react";

const PlanCardButton = ({
  workspace,
  isCurrentTier,
  hasConfigurationChanges,
  isCustomPlan,
  isYearlyIntervalUpgrade,
  isDowngrade,
  currentPlan,
  handleSubmitPlanChanges,
  isManagedByReplo,
  isPlanConfigurationValid,
  invalidPlanConfigurationTooltipContent,
  billingInterval,
  isSubscriptionDetailsPending,
  isPlanCancelledButNotYetEnded,
}: {
  workspace: Workspace;
  isCurrentTier: boolean;
  hasConfigurationChanges: boolean;
  isCustomPlan: boolean;
  isYearlyIntervalUpgrade: boolean;
  isDowngrade: boolean;
  currentPlan: V1BillingTier | V2BillingTier;
  handleSubmitPlanChanges: () => void;
  isManagedByReplo: boolean;
  isPlanConfigurationValid: boolean;
  invalidPlanConfigurationTooltipContent?: string;
  billingInterval: BillingInterval;
  isSubscriptionDetailsPending: boolean;
  isPlanCancelledButNotYetEnded: boolean;
}) => {
  const isWorkspaceOwner = useIsWorkspaceOwner(workspace.id);

  const { buttonText, buttonVariant } = getButtonTextAndVariantForPlanCard(
    isCurrentTier,
    hasConfigurationChanges,
    isCustomPlan,
    isYearlyIntervalUpgrade,
    isDowngrade,
    currentPlan,
    billingInterval,
    isPlanCancelledButNotYetEnded,
  );

  const { isDisabled, tooltipContent } = (() => {
    if (isCustomPlan) {
      const isDisabled = isCurrentTier && !hasConfigurationChanges;
      return {
        isDisabled,
        tooltipContent: isDisabled ? "This is your current plan" : undefined,
      };
    }

    const isCurrentPlanWithNoChanges =
      isCurrentTier && !hasConfigurationChanges;

    if (!isWorkspaceOwner) {
      return {
        isDisabled: true,
        tooltipContent: "Only workspace owners can change billing plans",
      };
    }

    if (isManagedByReplo) {
      return {
        isDisabled: true,
        tooltipContent:
          "Your workspace has a custom subscription. Please contact billing@replo.app to make changes.",
      };
    }

    if (!isPlanConfigurationValid) {
      return {
        isDisabled: true,
        tooltipContent: invalidPlanConfigurationTooltipContent,
      };
    }

    if (isCurrentPlanWithNoChanges && !isPlanCancelledButNotYetEnded) {
      return {
        isDisabled: true,
        tooltipContent: "This is your current plan",
      };
    }

    if (isSubscriptionDetailsPending) {
      return {
        isDisabled: true,
        tooltipContent: "Loading...",
      };
    }

    return {
      isDisabled: false,
      tooltipContent: undefined,
    };
  })();

  const handleSubmitCustomPlan = () => {
    window.open("https://replo.app/contact", "_blank");
    return;
  };

  return (
    <Button
      variant={buttonVariant}
      size="base"
      disabled={isDisabled}
      layoutClassName="w-full mt-auto"
      onClick={isCustomPlan ? handleSubmitCustomPlan : handleSubmitPlanChanges}
      tooltipText={tooltipContent}
    >
      {buttonText}
      {isCustomPlan && <ArrowUpRight className="ml-1 mt-0.5" size={14} />}
    </Button>
  );
};

export default PlanCardButton;

const getButtonTextAndVariantForPlanCard = (
  isCurrentTier: boolean,
  hasConfigurationChanges: boolean,
  isCustomPlan: boolean,
  isYearlyIntervalUpgrade: boolean,
  isDowngrade: boolean,
  currentPlan: V1BillingTier | V2BillingTier,
  billingInterval: BillingInterval,
  isPlanCancelledButNotYetEnded: boolean,
) => {
  let buttonText: string;
  let buttonVariant: ButtonVariant;

  if (isYearlyIntervalUpgrade) {
    buttonText = "Upgrade to Annual";
    buttonVariant = "primary";
  } else if (isCurrentTier && hasConfigurationChanges) {
    buttonText = "Update";
    buttonVariant = "primary";
  } else if (isCurrentTier && isPlanCancelledButNotYetEnded) {
    buttonText = "Resume Your Plan";
    buttonVariant = "primary";
  } else if (isCurrentTier) {
    buttonText = "Current Plan";
    buttonVariant = "secondary";
  } else if (isCustomPlan) {
    buttonText = "Let's Talk";
    buttonVariant = "primary";
  } else if (isDowngrade) {
    buttonText = "Downgrade";
    buttonVariant = "secondary";
  } else if (currentPlan === "free") {
    buttonText =
      billingInterval === "yearly"
        ? "Choose Annual Plan"
        : "Choose Monthly Plan";
    buttonVariant = "primary";
  } else {
    buttonText = "Upgrade";
    buttonVariant = "primary";
  }

  return {
    buttonText,
    buttonVariant,
  };
};
